import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { Copy, Database } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Button } from '../../../components/ui/button';
import { toast } from 'react-toastify';

// Define the Result type
interface Result {
  message: string;
  responseCode: number;
  responseID: number;
  extra?: string | null;
}

// GraphQL query
const GET_DATABASE_SCHEMA = gql`
  query GetDatabaseSchema {
    databaseSchema {
      message
      responseCode
      responseID
      extra
    }
  }
`;

export function DatabaseSchemaPage() {
  const { loading, error, data } = useQuery<{ databaseSchema: Result }>(GET_DATABASE_SCHEMA);
  const rawSchema = data?.databaseSchema?.extra || '';
  const parsedSchema = React.useMemo(() => {
    if (!rawSchema) {
      return null;
    }
    try {
      return JSON.parse(rawSchema);
    } catch {
      return null;
    }
  }, [rawSchema]);
  
  const copyToClipboard = () => {
    try {
      if (!parsedSchema) {
        throw new Error('Schema is not available');
      }
      const formattedJson = JSON.stringify(parsedSchema, null, 2);
      navigator.clipboard.writeText(formattedJson);
      toast.success("Schema copied to clipboard");
    } catch {
      toast.error("Failed to copy schema");
    }
  };

  return (
    <div className="container mx-auto p-4 space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-600" />
            <div>
              <CardTitle className="text-lg">Database Schema</CardTitle>
              <CardDescription className="text-sm text-gray-600">
                Current Prisma database schema (read-only)
              </CardDescription>
            </div>
          </div>
          {parsedSchema && (
            <Button
              id="database-schema-copy-btn"
              variant="outline"
              onClick={copyToClipboard}
              className="h-9 text-sm"
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && <div className="text-sm text-gray-600">Loading schema...</div>}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              Error: {error.message}
            </div>
          )}

          {data && !rawSchema && !error && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Database schema is not available.
            </div>
          )}

          {rawSchema && !parsedSchema && !error && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Database schema response was not valid JSON.
            </div>
          )}

          {parsedSchema && (
            <ScrollArea className="h-[520px] rounded-md border border-gray-200 bg-gray-50 p-3">
              <pre className="whitespace-pre-wrap font-mono text-xs text-gray-900">
                {JSON.stringify(parsedSchema, null, 2)}
              </pre>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 
